import { Form, useActionData } from "@remix-run/react";
import { trackEvent, EventCategory, EventAction } from '~/utils/analytics';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Field } from "~/components/forms";
import { z } from "zod";
import { useIsPending } from '~/utils/misc';
import { Separator } from "~/components/ui/separator";
import { SocialLoginButtons } from "~/components/socialLoginButtons";

const signupSchema = z.object({
  email: z.string().email("Por favor, introduce un email válido"),
  signupType: z.enum(['email', 'google', 'facebook']),
  redirectTo: z.string().optional(),
});

export function SignupForm({ redirectTo }: { redirectTo: string }) {
  const actionData = useActionData<{ status: string, email: string, error?: string }>();
  
  const [form, fields] = useForm({
    id: 'signup-form',
    defaultValue: { 
      redirectTo, 
      signupType: 'email'
    },
    constraint: getZodConstraint(signupSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: signupSchema });
    },
    shouldValidate: 'onSubmit',
  });

  const isPending = useIsPending();
  const sentSuccess = actionData?.status === 'success';

  // Handler for Google signup
  const handleGoogleSignup = () => {
    trackEvent({
      category: EventCategory.USER,
      action: EventAction.CLICK,
      label: 'google-signup',
    });
    
    const form = document.getElementById('signup-form') as HTMLFormElement;
    const signupTypeInput = form.querySelector('input[name="signupType"]') as HTMLInputElement;
    signupTypeInput.value = 'google';
    form.action = '/login';
    form.submit();
  };

  // Handler for Facebook signup
  const handleFacebookSignup = () => {
    trackEvent({
      category: EventCategory.USER,
      action: EventAction.CLICK,
      label: 'facebook-signup',
    });
    
    const form = document.getElementById('signup-form') as HTMLFormElement;
    const signupTypeInput = form.querySelector('input[name="signupType"]') as HTMLInputElement;
    signupTypeInput.value = 'facebook';
    form.action = '/login';
    form.submit();
  };

  const getSubmitButtonText = () => {
    if (isPending) return 'Procesando...';
    return 'Enviar enlace de verificación';
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-bold">Crear cuenta</h2>
        <p className="text-muted-foreground">Introduce tu email para crear tu cuenta</p>
      </div>
      <div className="space-y-4">
        {/* Social Signup Buttons */}
        <div className="space-y-3">
          <SocialLoginButtons
            onGoogleLogin={handleGoogleSignup}
            onFacebookLogin={handleFacebookSignup}
            isPending={isPending}
            buttonText={{
              google: "Continuar con Google",
              facebook: "Continuar con Facebook"
            }}
          />
        </div>

        {/* Separator */}
        <Separator className="w-full bg-neutral-400 mb-16" style={{ margin: "24px 0" }}/>

        {/* Simple Email Signup Form */}
        <Form
          method="POST"
          action="/login"
          {...getFormProps(form)}
          onSubmit={() => {
            trackEvent({
              category: EventCategory.FORM,
              action: EventAction.SUBMIT,
              label: 'signup-email',
            });
          }}
        >
          <CardDescription className="mb-4">o Introduce tu email</CardDescription>

          <div className="space-y-4">
            <AuthenticityTokenInput />
            <input type="hidden" name="signupType" value="email" />
            <input type="hidden" name="redirectTo" value={redirectTo} />
            
            {/* Email Field - ONLY INPUT */}
            <Field 
              labelProps={{ 
                htmlFor: fields.email.id, 
                children: 'Email' 
              }}
              inputProps={{
                ...getInputProps(fields.email, { type: 'email' }),
                placeholder: 'tu@email.com',
                autoComplete: 'email'
              }}
              errors={fields.email.errors}
            />

            {/* Error Message */}
            {actionData?.error && (
              <p className="text-sm text-destructive text-center">
                {actionData.error}
              </p>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isPending}
            >
              {getSubmitButtonText()}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Te enviaremos un enlace para verificar tu email y completar tu perfil
            </p>
          </div>
        </Form>

        {/* Success Message */}
        {sentSuccess && (
          <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
            <p className="text-sm text-center">
              Te hemos enviado un enlace de verificación a: <strong>{actionData?.email}</strong>
            </p>
            <p className="text-sm text-center mt-2">
              Revisa tu bandeja de entrada y haz clic en el enlace para completar tu registro.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
